<script>
import InfoExclamationMark from "@/components/info/infoExclamationMark.vue"

export default {
  name: "FormSelect",
  components: { InfoExclamationMark },
  emits: ["change", "mouseup", "customActionClicked", "clear"],
  props: {
    customActionText: {
      type: String,
      required: false,
      default: ""
    },
    hasError: {
      type: Boolean,
      default: false
    },
    infoText: {
      type: String,
      default: ""
    },
    infoTooltip: {
      type: String,
      default: ""
    },
    items: {
      type: Array,
      required: true
    },
    itemSubtext: {
      type: String,
      required: false,
      default: ""
    },
    itemText: {
      type: String,
      required: true
    },
    itemValue: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false,
      default: ""
    },
    large: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      required: false,
      default: ""
    },
    sideBox: {
      type: Boolean,
      default: false
    },
    value: {
      type: [Object, Number],
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    subtextFontSize: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    isOpened: false,
    search: ""
  }),
  computed: {
    valueObj() {
      if (typeof this.value === "object") {
        return this.value
      }

      return this.items.find((item) => item[this.itemValue] === this.value)
    }
  },
  methods: {
    open() {
      if (!this.isOpened) {
        this.search = ""
        this.isOpened = true
        setTimeout(() => {
          this.$refs.searchInput.focus()
        }, 200)
      } else {
        this.isOpened = false
      }
    }
  }
}
</script>

<template>
  <div>
    <div :class="{ 'side-box__row d-flex align-items-center mt-3': sideBox }">
      <div
        v-if="label || customActionText"
        class="font-weight-light mb-1 d-flex justify-content-between"
        :class="{
          'side-box__content__label mr-3 pr-1': sideBox,
          'text-left': !sideBox
        }"
      >
        <span v-if="label">
          {{ label }}
        </span>
        <a @click.prevent="$emit('customActionClicked')">
          {{ customActionText }}
        </a>
      </div>
      <div
        v-click-outside="() => (isOpened = false)"
        class="custom-control__select w-100"
        :class="{ 'side-box__content__input': sideBox }"
      >
        <div
          class="custom-control__select__selected d-flex"
          :class="{
            'has-error': hasError,
            opened: isOpened,
            disabled: disabled,
            clearable
          }"
          @click="disabled ? '' : open()"
        >
          <img
            v-if="clearable && valueObj"
            class="clear-icon"
            src="@/assets/icons/actions/ico-close.svg"
            @click.stop="$emit('clear')"
          />
          <div class="d-flex flex-wrap w-100" :class="{ 'py-2': large }">
            <span v-if="!valueObj && !isOpened" class="gray-text">{{ placeholder }}</span>
            <div v-else-if="!isOpened" class="d-flex align-items-center w-100">
              <span v-if="valueObj.atomCircleClass" :class="`atom-circle ${valueObj.atomCircleClass} mr-2`">
                {{ valueObj["atomCircleText"] || valueObj[itemValue] }}
              </span>
              <span class="font-weight-light">
                {{ valueObj[itemText] }}
                <br />
                <span v-if="itemSubtext" class="text-smallest" :style="{ 'font-size': subtextFontSize, 'white-space': 'pre-line' }">
                  {{ valueObj[itemSubtext] }}
                </span>
              </span>
            </div>
            <input
              v-show="isOpened"
              ref="searchInput"
              :value="this.search"
              @input="this.search = $event.target.value"
            />
          </div>
        </div>
        <div class="custom-control__select__options" :class="{ opened: isOpened }">
          <div
            v-for="item of items.filter((item) => item[itemText].toLowerCase().includes(search.toLowerCase()))"
            :key="`${label}Option${item[itemValue]}`"
            class="custom-control__select__option d-flex align-items-center"
            @click="
              () => {
                $emit('change', item)
                isOpened = false
              }
            "
            @mouseup="$emit('mouseup', { value: item, nativeEvent: $event })"
          >
            <div class="d-flex justify-content-around w-100 pr-2">
              <div class="check">
                <img
                  src="@/assets/icons/check.svg"
                  :class="{
                    invisible: !valueObj || valueObj[itemValue] !== item[itemValue]
                  }"
                />
              </div>
              <div class="d-flex w-100">
                <span v-if="item.atomCircleClass" :class="`atom-circle ${item.atomCircleClass} mr-2`">
                  {{ item["atomCircleText"] || item[itemValue] }}
                </span>
                <span class="font-weight-light">
                  {{ item[itemText] }}
                  <br />
                  <span v-if="itemSubtext" class="text-smallest" :style="{ 'font-size': subtextFontSize, 'white-space': 'pre-line' }">
                    {{ item[itemSubtext] }}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div v-if="!items.length" class="custom-control__select__option d-flex align-items-center gray-text">
            {{ $t("SELECT_NO_DATA") }}
          </div>
        </div>
      </div>
    </div>
    <InfoExclamationMark v-if="infoTooltip && infoTooltip" :info-text="infoText" :info-tooltip="infoTooltip" />
  </div>
</template>
